import React from 'react';
import { Button, Divider, Stack, Typography } from '@mui/material';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { PaperCard } from 'components/Card';
import { EXTERNAL, paths } from 'routes';
import { FormProvider, TextField } from 'components/Form';
import useYupValidationResolver from 'hook/useYupValidationResolver';
import { personalDetailsSchema } from '../validation';
import { BlueMessageBar } from 'components/BlueMessageBar';
import { TurnstileWidget } from 'components/TurnstileWidget'; // Added

export const PersonalDetailsForm = ({
  nextStep,
  handleChange,
  values,
  acceptInvitation = false,
  onTurnstileVerify, // Added prop
  isTurnstileVerified, // Added prop
}) => {
  const { t } = useTranslation();
  const methods = useForm({
    mode: 'onSubmit',
    resolver: useYupValidationResolver(personalDetailsSchema),
    values: values,
  });

  const onSubmit = async (data) => {
    nextStep();
    /* try {
      methods.setError(null)
      await signUp(data);
    } catch (error) {
      methods.setError("password", { type: "custom", message: error.message });
    } */
  };

  

  const message = t('website:auth.register.personalDetails.buyersMessage');

  return (
    <PaperCard width={{ xs: 1, sm: 368 }} maxWidth={{ sm: 368 }} sx={{mb:5}}>
      <Stack>
        <Typography variant="h5" sx={{ mb: 0, textAlign: "center" }}>
          {t('website:auth.register.personalDetails.title')}
          <BlueMessageBar message={message} href={paths.auth.buyersRegister} sx={{textAlign: "left"}} />
        </Typography>

        <FormProvider methods={methods} onSubmit={onSubmit}>
          <Stack>
            <TextField
              name="firstName"
              label={t('website:auth.register.personalDetails.fields.firstName.label')}
              placeholder={t('website:auth.register.personalDetails.fields.firstName.placeholder')}
              required={true}
              type="text"
              autoComplete="given-name"
              value={values.firstName}
              onChange={handleChange('firstName')}
              sx={{ mb: 2 }}
              fullWidth
              disabled={acceptInvitation}
            />
            <TextField
              name="lastName"
              label={t('website:auth.register.personalDetails.fields.lastName.label')}
              placeholder={t('website:auth.register.personalDetails.fields.lastName.placeholder')}
              required={true}
              type="text"
              autoComplete="family-name"
              value={values.lastName}
              onChange={handleChange('lastName')}
              sx={{ mb: 2 }}
              fullWidth
              disabled={acceptInvitation}
            />
            <TextField
              name="phoneNumber"
              label={t('website:auth.register.personalDetails.fields.phoneNumber.label')}
              placeholder={t('website:auth.register.personalDetails.fields.phoneNumber.placeholder')}
              required={true}
              type="tel"
              autoComplete="tel"
              value={values.phoneNumber}
              onChange={handleChange('phoneNumber')}
              sx={{ mb: 2 }}
              fullWidth
            />
            <TextField
              name="companyEmail"
              label={t('website:auth.register.personalDetails.fields.companyEmail.label')}
              placeholder={t('website:auth.register.personalDetails.fields.companyEmail.placeholder')}
              required={true}
              type="email"
              autoComplete="email"
              value={values.companyEmail}
              onChange={handleChange('companyEmail')}
              sx={{ mb: 2 }}
              fullWidth
              disabled={acceptInvitation}
            />
            <TextField
              name="password"
              label={t('website:auth.register.personalDetails.fields.password.label')}
              placeholder={t('website:auth.register.personalDetails.fields.password.placeholder')}
              required={true}
              type="password"
              value={values.password}
              onChange={handleChange('password')}
              error={Boolean(methods?.formState?.errors?.password)}
              helperText={methods?.formState?.errors?.password?.message}
              sx={{ mb: 2 }}
              fullWidth
            />
            <TextField
              name="confirmPassword"
              label={t('website:auth.register.personalDetails.fields.confirmPassword.label')}
              placeholder={t('website:auth.register.personalDetails.fields.confirmPassword.placeholder')}
              required={true}
              type="password"
              value={values.confirmPassword}
              onChange={handleChange('confirmPassword')}
              error={Boolean(methods?.formState?.errors?.confirmPassword)}
              helperText={methods?.formState?.errors?.confirmPassword?.message}
              sx={{ mb: 1 }}
              fullWidth
            />

            <TurnstileWidget onSuccess={onTurnstileVerify} /> {/* Added */}

            <Button
              size="large"
              type="submit"
              variant="contained"
              color="primary"
              disabled={!isTurnstileVerified || acceptInvitation} // Modified disabled prop
              sx={{
                mt: 2,
                mb: 0.5,
                bgcolor: "primary.light",
                '&:hover': {
                  backgroundColor: "primary.light",
                },
              }}
            >
              {acceptInvitation
                ? t('website:auth.register.personalDetails.saveButton')
                : t('website:auth.register.personalDetails.nextButton')}
            </Button>
            <Stack
              align="center"
              gap={1.5}
              sx={{
                a: {
                  color: 'inherit',
                  fontWeight: 700,
                  textTransform: 'none',
                },
              }}
            >
              <Typography variant="caption" sx={{ fontWeight: 400, color: "text.primary" }}>
                {t('website:auth.register.personalDetails.termsAgreement.before')}{' '}
                <a href={paths.website.termsAndConditions}  target='_blank' rel="noreferrer">{t('website:auth.register.personalDetails.termsAgreement.linkText')}</a>.
                {/* {' and '} */}
                {/* <a href={EXTERNAL.PRIVACY_POLICE}  target='_blank' rel="noreferrer">Privacy Policy</a> */}
              </Typography>

              {!acceptInvitation && (
                <>
                  <Divider sx={{ width: 1 }} />

                  <Typography variant="caption" sx={{ fontWeight: 400, color: "text.primary" }}>
                    {t('website:auth.register.personalDetails.alreadyMemberTextBefore')} <Link to={paths.auth.login}>{t('website:auth.register.personalDetails.alreadyMemberLinkText')}</Link>
                  </Typography>
                </>
              )}
            </Stack>
          </Stack>
        </FormProvider>
      </Stack>
    </PaperCard>
  );
};
